import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import strings from 'common/Translation/Translate';
import * as React from 'react';

export type ConfirmModalTone = 'default' | 'warning' | 'finalize' | 'danger';

interface ConfirmModalProps {
  handleYes: () => void;
  open: boolean;
  closeModal: () => void;
  message?: string;
  title?: string;
  subtitle?: string;
  /** Highlighted entity/item name shown in a callout under the subtitle. */
  entityName?: string;
  /** Extra body content (e.g. dropdown) shown under the message. */
  children?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ConfirmModalTone;
  loading?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md';
  /** Overrides the tone icon. */
  icon?: React.ReactNode;
  confirmDisabled?: boolean;
  confirmAutoFocus?: boolean;
}

const toneConfig: Record<
  ConfirmModalTone,
  {
    icon: React.ReactNode;
    iconBg: string;
    iconColor: string;
    accent: string;
    accentHover: string;
    headerBg: string;
    calloutBg: string;
    calloutBorder: string;
  }
> = {
  default: {
    icon: <WarningAmberOutlinedIcon sx={{ fontSize: 28 }} />,
    iconBg: 'rgba(25, 118, 210, 0.12)',
    iconColor: '#1967D2',
    accent: 'primary.main',
    accentHover: 'primary.dark',
    headerBg: 'linear-gradient(180deg, rgba(25, 118, 210, 0.06) 0%, transparent 100%)',
    calloutBg: 'rgba(25, 118, 210, 0.06)',
    calloutBorder: 'rgba(25, 118, 210, 0.25)',
  },
  warning: {
    icon: <WarningAmberOutlinedIcon sx={{ fontSize: 28 }} />,
    iconBg: 'rgba(237, 108, 2, 0.12)',
    iconColor: '#ED6C02',
    accent: 'warning.main',
    accentHover: 'warning.dark',
    headerBg: 'linear-gradient(180deg, rgba(237, 108, 2, 0.08) 0%, transparent 100%)',
    calloutBg: 'rgba(237, 108, 2, 0.08)',
    calloutBorder: 'rgba(237, 108, 2, 0.35)',
  },
  finalize: {
    icon: <LockOutlined sx={{ fontSize: 26 }} />,
    iconBg: 'rgba(128, 0, 128, 0.12)',
    iconColor: '#800080',
    accent: '#800080',
    accentHover: '#6A1B9A',
    headerBg: 'linear-gradient(180deg, rgba(128, 0, 128, 0.08) 0%, transparent 100%)',
    calloutBg: 'rgba(128, 0, 128, 0.06)',
    calloutBorder: 'rgba(128, 0, 128, 0.2)',
  },
  danger: {
    icon: <DeleteOutlineOutlinedIcon sx={{ fontSize: 28 }} />,
    iconBg: 'rgba(211, 47, 47, 0.12)',
    iconColor: '#D32F2F',
    accent: 'error.main',
    accentHover: 'error.dark',
    headerBg: 'linear-gradient(180deg, rgba(211, 47, 47, 0.08) 0%, transparent 100%)',
    calloutBg: 'rgba(211, 47, 47, 0.06)',
    calloutBorder: 'rgba(211, 47, 47, 0.28)',
  },
};

function splitMessage(message?: string): string[] {
  if (!message?.trim()) return [];
  return message
    .split(/\n\n+/)
    .map(p => p.trim())
    .filter(Boolean);
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  handleYes,
  open,
  closeModal,
  message,
  title,
  subtitle,
  entityName,
  children,
  confirmLabel = 'Yes',
  cancelLabel = strings.cancelText,
  tone = 'warning',
  loading = false,
  maxWidth = 'xs',
  icon,
  confirmDisabled = false,
  confirmAutoFocus = true,
}) => {
  const config = toneConfig[tone];
  const paragraphs = splitMessage(message);
  const headline = title ?? strings.areYouSureText;
  const callout = paragraphs.length > 1 ? paragraphs[0] : null;
  const bodyParts = paragraphs.length > 1 ? paragraphs.slice(1) : paragraphs;
  const showContent = Boolean(callout || bodyParts.length > 0 || entityName || children);

  return (
    <Dialog
      open={open}
      maxWidth={maxWidth}
      fullWidth
      onClose={loading ? undefined : closeModal}
      aria-labelledby="confirm-modal-title"
      aria-describedby="confirm-modal-description"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.14)',
        },
      }}
      sx={{
        // Master Data drawers use modal + 1; keep confirmations above them.
        zIndex: theme => theme.zIndex.modal + 2,
      }}>
      <Box
        sx={{
          px: 2.5,
          pt: 2.5,
          pb: 1.5,
          background: config.headerBg,
        }}>
        <Stack direction="row" spacing={1.75} alignItems="flex-start">
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              bgcolor: config.iconBg,
              color: config.iconColor,
            }}>
            {icon ?? config.icon}
          </Box>
          <Box sx={{ minWidth: 0, pt: 0.25 }}>
            <Typography id="confirm-modal-title" variant="h6" fontWeight={700} lineHeight={1.25} fontSize="1.125rem">
              {headline}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.45 }}>
                {subtitle}
              </Typography>
            ) : null}
            {tone === 'finalize' && !subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {strings.finalize_actualization_button}
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>

      {showContent && (
        <DialogContent id="confirm-modal-description" sx={{ px: 2.5, pt: 1, pb: 1.5 }}>
          {entityName ? (
            <Box
              sx={{
                mb: callout || bodyParts.length > 0 ? 1.5 : 0,
                px: 1.75,
                py: 1.25,
                borderRadius: 2,
                bgcolor: config.calloutBg,
                border: '1px solid',
                borderColor: config.calloutBorder,
              }}>
              <Typography variant="body2" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.5 }}>
                {entityName}
              </Typography>
            </Box>
          ) : null}
          {callout && (
            <Box
              sx={{
                mb: 1.5,
                px: 1.75,
                py: 1.25,
                borderRadius: 2,
                bgcolor: config.calloutBg,
                border: '1px solid',
                borderColor: config.calloutBorder,
              }}>
              <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ lineHeight: 1.5 }}>
                {callout}
              </Typography>
            </Box>
          )}
          {bodyParts.map((part, index) => (
            <Typography
              key={index}
              variant="body2"
              color="text.secondary"
              sx={{
                lineHeight: 1.55,
                mb: index < bodyParts.length - 1 || children ? 1.25 : 0,
              }}>
              {part}
            </Typography>
          ))}
          {children}
        </DialogContent>
      )}

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.75,
          gap: 1,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={closeModal}
          disabled={loading}
          sx={{
            minWidth: 96,
            textTransform: 'none',
            fontWeight: 600,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}>
          {cancelLabel}
        </Button>
        <Button
          variant="contained"
          onClick={handleYes}
          autoFocus={confirmAutoFocus}
          disabled={loading || confirmDisabled}
          sx={{
            minWidth: 120,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: 'none',
            bgcolor: config.accent,
            '&:hover': {
              bgcolor: config.accentHover,
              boxShadow: 'none',
            },
          }}>
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmModal;
